'use client';

/**
 * "At your seat": the room's before/after, as the listening room's readout.
 *
 * The same curve the pre-upsell draws (upsell/series.ts, one reading of the
 * room's solved snapshots), shown as the hero's seat card is shown: a card
 * that floats over the room (its host decides where it floats and so where
 * it is glass). It follows the Before/After the room and
 * the audio follow — the state on screen is drawn in full ink, the other one
 * steps back — so the picture, the sound and the curve change as one.
 *
 * The number is the room's own headline figure (upsell/content.ts, the
 * problem each room is bought to solve): bass swing for the hi-fi room, decay
 * for the theater, the response window for the studio.
 */

import { useMemo } from 'react';
import {
  createLinearScale,
  createLogFreqScale,
  createPlotFrame,
  linearToPlot,
  linearTicks,
  logFreqToX,
} from '@roomtreatment/charts';
import type { ABState } from '@/components/upsell/BeforeAfter';
import type { ShowcaseSlice } from '@/components/landing/showcase/showcaseTabs';
import { buildSeries, GRID_MAJOR, TARGET, type Point } from '@/components/upsell/series';
import { useHostWidth } from '@/components/upsell/useHostWidth';

const HEIGHT = 96;
const PAD = { t: 4, r: 0, b: 4, l: 0 };

export default function SeatCard({ slice, state, className = '' }: {
  slice: ShowcaseSlice;
  state: ABState;
  className?: string;
}) {
  const [hostRef, width] = useHostWidth();
  const kind = slice.copy.curve;
  const s = useMemo(() => buildSeries(slice.room, kind), [slice.room, kind]);
  const after = state === 'after';

  const plot = useMemo(() => {
    if (width <= 0) return null;
    const frame = createPlotFrame(width, HEIGHT, PAD);
    const xs = createLogFreqScale(frame.plotW, s.fLo, s.fHi);
    const ys = createLinearScale(frame.plotH, s.yLo, s.yHi);
    return {
      X: (f: number) => frame.plotX + logFreqToX(xs, f),
      Y: (v: number) => frame.plotY + linearToPlot(ys, v),
      ...frame,
    };
  }, [width, s]);

  const path = (key: 'before' | 'after') =>
    plot
      ? s.points.map((p: Point, i) => `${i ? 'L' : 'M'}${plot.X(p.f).toFixed(1)},${plot.Y(p[key]).toFixed(1)}`).join(' ')
      : '';

  return (
    <div data-testid="home-listen-seat" className={`rounded-2xl p-5 ${className}`}>
      <p className="text-overline font-semibold uppercase text-[var(--rt-ink-dim)]">{slice.copy.figure.label}</p>
      <p className="mt-1 whitespace-nowrap text-title font-semibold tabular-nums">
        <span className={`transition-colors duration-[var(--rt-fade-ms)] ${after ? 'text-[var(--rt-ink-faint)]' : 'text-[var(--rt-ink)]'}`}>
          {slice.copy.figure.before}
        </span>
        <span aria-hidden className="mx-2 text-[var(--rt-ink-faint)]">→</span>
        <span className={`transition-colors duration-[var(--rt-fade-ms)] ${after ? 'text-[var(--rt-ink)]' : 'text-[var(--rt-ink-faint)]'}`}>
          {slice.copy.figure.after}
        </span>
      </p>

      {/* Holds the plot's height until the host is measured (CLS). */}
      <div ref={hostRef} className="mt-4" style={{ height: HEIGHT }}>
        {plot && (
          <svg
            width={width}
            height={HEIGHT}
            viewBox={`0 0 ${width} ${HEIGHT}`}
            role="img"
            aria-label={`${slice.copy.figure.label} at the seat: ${slice.copy.figure.before} before treatment, ${slice.copy.figure.after} after.`}
            className="block overflow-visible"
          >
            <rect
              x={plot.plotX}
              width={plot.plotW}
              y={plot.Y(TARGET[kind].hi)}
              height={Math.abs(plot.Y(TARGET[kind].lo) - plot.Y(TARGET[kind].hi))}
              fill="color-mix(in oklch, var(--rt-ok) 14%, transparent)"
            />
            {linearTicks(s.yLo, s.yHi, s.yMajor).map((v) => (
              <line
                key={v}
                x1={plot.plotX} x2={plot.plotX + plot.plotW}
                y1={plot.Y(v)} y2={plot.Y(v)}
                stroke={GRID_MAJOR}
              />
            ))}
            {kind === 'rt60' ? (
              s.points.map((p) => {
                const w = Math.max(4, (plot.plotW / s.points.length) * 0.3);
                const base = plot.Y(s.yLo);
                return (
                  <g key={p.f}>
                    <rect
                      x={plot.X(p.f) - w} width={w}
                      y={plot.Y(p.before)} height={Math.max(0, base - plot.Y(p.before))}
                      fill="var(--rt-ink-faint)"
                      className="transition-opacity duration-[var(--rt-fade-ms)]"
                      style={{ opacity: after ? 0.35 : 0.9 }}
                    />
                    <rect
                      x={plot.X(p.f)} width={w}
                      y={plot.Y(p.after)} height={Math.max(0, base - plot.Y(p.after))}
                      fill="var(--rt-ink)"
                      className="transition-opacity duration-[var(--rt-fade-ms)]"
                      style={{ opacity: after ? 1 : 0.35 }}
                    />
                  </g>
                );
              })
            ) : (
              <>
                <path
                  d={path('before')} fill="none" stroke="var(--rt-ink-faint)"
                  strokeWidth={1.6} strokeLinejoin="round" strokeLinecap="round"
                  className="transition-opacity duration-[var(--rt-fade-ms)]"
                  style={{ opacity: after ? 0.45 : 1 }}
                />
                <path
                  d={path('after')} fill="none" stroke="var(--rt-ink)"
                  strokeWidth={2.4} strokeLinejoin="round" strokeLinecap="round"
                  className="transition-opacity duration-[var(--rt-fade-ms)]"
                  style={{ opacity: after ? 1 : 0.35 }}
                />
              </>
            )}
          </svg>
        )}
      </div>
    </div>
  );
}
